/**
 * @file QuickActions.tsx
 * @description Ряд «Быстрые действия» на «Обзоре» платформы (phase19.29).
 *
 * Плитки — ссылки в существующие разделы, ничего не запускают прямо с
 * «Обзора» (копирование, создание и т.п. делаются в разделе, где видны
 * последствия). Каждая плитка — под своим правом, как пункт меню. Список —
 * реестр `QUICK_ACTIONS`: новое действие = запись.
 */

import { DatabaseBackup, PlugZap, UserPlus, type LucideIcon } from "lucide-react";
import { Link } from "react-router";

import { useHasPermission } from "../../../auth/hooks";

/** Права разделов платформы, на которые ведут быстрые действия. */
type QuickPermission =
  | "PLATFORM_MANAGE_USERS"
  | "PLATFORM_MANAGE_INTEGRATIONS"
  | "PLATFORM_MANAGE_BACKUPS";

interface QuickAction {
  to: string;
  label: string;
  icon: LucideIcon;
  permission: QuickPermission;
}

const QUICK_ACTIONS: QuickAction[] = [
  {
    to: "/platform/users",
    label: "Пользователи и доступы",
    icon: UserPlus,
    permission: "PLATFORM_MANAGE_USERS",
  },
  {
    to: "/platform/integrations",
    label: "Подключение интеграций",
    icon: PlugZap,
    permission: "PLATFORM_MANAGE_INTEGRATIONS",
  },
  {
    to: "/platform/backups",
    label: "Резервные копии",
    icon: DatabaseBackup,
    permission: "PLATFORM_MANAGE_BACKUPS",
  },
];

export function QuickActions() {
  // Права — общим хуком (как меню); по одному вызову на право, без циклов.
  const granted: Record<QuickPermission, boolean> = {
    PLATFORM_MANAGE_USERS: useHasPermission("PLATFORM_MANAGE_USERS"),
    PLATFORM_MANAGE_INTEGRATIONS: useHasPermission("PLATFORM_MANAGE_INTEGRATIONS"),
    PLATFORM_MANAGE_BACKUPS: useHasPermission("PLATFORM_MANAGE_BACKUPS"),
  };
  const allowed = QUICK_ACTIONS.filter((a) => granted[a.permission]);
  // Нет ни одного доступного раздела — блок не показываем.
  if (allowed.length === 0) return null;

  return (
    <section aria-labelledby="overview-quick" className="flex flex-col gap-3">
      <h2 id="overview-quick" className="type-section-title text-on-surface">
        Быстрые действия
      </h2>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {allowed.map((action) => {
          const Icon = action.icon;
          return (
            <Link
              key={action.to}
              to={action.to}
              className="flex min-h-14 items-center gap-3 rounded-xl bg-card px-4 py-3 text-sm font-medium text-on-surface ring-1 ring-outline-variant outline-none card-interactive sm:flex-col sm:items-start sm:py-4"
            >
              <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-accent text-accent-foreground">
                <Icon className="size-4" aria-hidden />
              </span>
              {action.label}
            </Link>
          );
        })}
      </div>
    </section>
  );
}
